// Alpine component behind the subscription checkout wizard.
//
// Holds the whole selection (product, then tier -> platform count or the GDPR package, then billing cycle) plus which
// accordion section is open, and derives every price shown on the page from the
// catalogue the server rendered. Display only: `create` re-resolves the package
// and re-computes the price server-side, so nothing here is trusted as input.

export default function checkoutWizard(config) {
  return {
    catalogue: config.catalogue || {},
    gdprCatalogue: config.gdprCatalogue || [],
    product: config.selection.product || "legal_texts",
    gdprId: config.selection.gdpr_id,
    tier: config.selection.tier,
    platforms: config.selection.platforms,
    cycle: config.selection.cycle,
    cycles: config.cycles || [1, 3, 6, 12],

    // Which accordion section is expanded: package | company | billing.
    section: "package",
    // Which sub-section of "Choose a package" is expanded: plan | platforms | cycle.
    step: "plan",

    collectionMethod: "charge_automatically",
    submitting: false,

    // Company chooser: which saved company is billed, keyed catalogue of all of
    // them, and the field values the form posts for the chosen one. `company`
    // is also what the summary's live VAT estimate reads, so swapping companies
    // re-prices the tax lines as well as refilling the form.
    legalEntityId: String(config.legalEntityId || "new"),
    companies: config.companies || {},
    company: {},

    // Pay by invoice is sold on the annual cycle only, as in the legacy shop:
    // access waits for the invoice to settle (pay-first), and that wait is only
    // acceptable once a year. Switching to a shorter cycle drops the choice.
    invoiceCycle: config.invoiceCycle || 12,

    vatRule: config.vat || { noVatIdRate: 19, domesticCountry: "DE", ratesByCountry: {}, noVatIdRatesByCountry: {}, reverseChargeCountries: [] },

    // The server prices a code for the current selection, so changing it asks again.
    promoInput: (config.promotion && config.promotion.code) || "",
    promo: null,
    promoError: null,
    promoBusy: false,
    promoRequest: 0,

    init() {
      this.chooseCompany(this.legalEntityId)
      this.$watch("cycle", () => {
        if (!this.invoiceAllowed && this.collectionMethod === "send_invoice") {
          this.collectionMethod = "charge_automatically"
        }
      })
      this.keepOfferedCycle()
      for (const key of ["product", "gdprId", "tier", "platforms"]) this.$watch(key, () => this.keepOfferedCycle())
      for (const key of ["product", "gdprId", "tier", "platforms", "cycle", "legalEntityId"]) {
        this.$watch(key, () => { if (this.promo || this.promoError) this.applyPromotion() })
      }
      if (this.promoInput) this.applyPromotion()
    },

    async applyPromotion() {
      const code = this.promoInput.trim()
      if (!code) return this.removePromotion()

      const request = ++this.promoRequest
      this.promoBusy = true
      const query = new URLSearchParams({ code, product: this.product, package_id: this.product === "gdpr" ? this.gdprId : "",
                                          tier: this.tier, platforms: this.platforms, cycle: this.cycle, legal_entity_id: this.legalEntityId })
      let answer
      try {
        const response = await fetch(`${config.promotion.url}?${query}`, { headers: { Accept: "application/json" } })
        answer = await response.json()
      } catch (e) {
        answer = { valid: false, message: config.promotion.unavailable }
      }
      if (request !== this.promoRequest) return

      this.promoBusy = false
      this.promo = answer.valid ? answer : null
      this.promoError = answer.valid ? null : answer.message
      if (!this.paymentRequired) this.collectionMethod = "charge_automatically"
    },

    removePromotion() {
      this.promoRequest++
      this.promoBusy = false
      this.promo = null
      this.promoError = null
      this.promoInput = ""
    },

    // A code that covers every invoice needs no payment method at all.
    get paymentRequired() {
      return !this.promo || this.promo.payment_required
    },

    get netTotal() {
      if (this.promo) return this.promo.net
      return this.pricing ? this.pricing.total : 0
    },

    // Only a company that already carries invoices is uneditable: it shows as a
    // summary and its inputs are disabled, so the checkout posts no fields for
    // it at all. Everything else is an editable form prefilled from the row.
    get companyEditable() {
      return !(this.companies[this.legalEntityId] || {}).billed
    },

    get companyAddress() {
      const c = this.company
      return [[c.street, c.street_no].filter(Boolean).join(" "),
              [c.postal_code, c.city].filter(Boolean).join(" "),
              c.country].filter(Boolean).join(", ")
    },

    chooseCompany(id) {
      this.legalEntityId = String(id)
      this.company = { ...((this.companies[this.legalEntityId] || {}).fields || {}) }
    },

    // Why a company cannot buy the chosen product (GDPR already held or in its plan, an unpaid order), or null.
    blockedFor(id) {
      return ((this.companies[String(id)] || {}).blocked || {})[this.product] || null
    },

    blockedLabel(id) {
      return config.blockedLabels[this.blockedFor(id)] || ""
    },

    // Every saved company is blocked for it: the product cannot be bought here.
    productBlocked(line) {
      const ids = Object.keys(this.companies).filter((id) => id !== "new")
      return ids.length > 0 && ids.every((id) => ((this.companies[id] || {}).blocked || {})[line])
    },

    productBlockedLabel(line) {
      const blocked = (this.companies[this.legalEntityId] || Object.values(this.companies)[0] || {}).blocked || {}
      return config.blockedLabels[blocked[line]] || ""
    },

    // A company the new product cannot be bought for gives way to the first one it can.
    selectProduct(line) {
      if (this.productBlocked(line)) return
      this.product = line
      this.step = "plan"
      if (this.blockedFor(this.legalEntityId)) {
        const open = Object.keys(this.companies).find((id) => id !== "new" && !this.blockedFor(id))
        this.chooseCompany(open || "new")
      }
    },

    get invoiceAllowed() {
      return Number(this.cycle) === Number(this.invoiceCycle)
    },

    get tiers() {
      return Object.keys(this.catalogue)
    },

    get plans() {
      return this.catalogue[this.tier] || []
    },

    // The package matching the chosen platform count, falling back to the
    // entry plan when a tier doesn't offer that count; for GDPR the chosen package.
    get plan() {
      if (this.product === "gdpr") return this.gdprCatalogue.find((p) => p.id === this.gdprId) || this.gdprCatalogue[0]
      return this.plans.find((p) => p.platforms === this.platforms) || this.plans[0]
    },

    // A package that does not sell the chosen cycle falls back to its shortest.
    keepOfferedCycle() {
      if (!this.offeredCycles.includes(this.cycle)) this.cycle = this.offeredCycles[this.offeredCycles.length - 1]
    },

    // The cycles the chosen package sells, longest first; one it does not offer has no price to show.
    get offeredCycles() {
      const offered = (this.plan && this.plan.offered) || this.cycles
      return [...this.cycles].reverse().filter((m) => offered.includes(m))
    },

    // Ruby hash keys arrive as JSON strings.
    get pricing() {
      return this.plan ? this.plan.cycles[String(this.cycle)] : null
    },

    // Display-only estimate, mirroring Pricing::VatEstimate.
    get vat() {
      const net = this.netTotal
      const currency = this.pricing ? this.pricing.currency : "EUR"
      const country = (this.company.country || "").toUpperCase()
      const hasVatId = !!(this.company.vat_id && this.company.vat_id.trim())
      const rate = hasVatId
        ? (this.vatRule.ratesByCountry[country] ?? 0)
        : (this.vatRule.noVatIdRatesByCountry?.[country] ?? this.vatRule.noVatIdRate)
      const amount = this.round2((net * rate) / 100)
      const reverseCharge = hasVatId && (this.vatRule.reverseChargeCountries || []).includes(country)
      return { rate, amount, gross: this.round2(net + amount), reverseCharge, currency }
    },

    round2(value) {
      return Math.round((value + Number.EPSILON) * 100) / 100
    },

    get vatLabel() {
      return config.labels.vat.replace("%{rate}", this.formatPercent(this.vat.rate))
    },

    // The chosen cycle's adjustment/savings label, from the ladder percentages
    // the server already computed — never recomputed here.
    get adjustmentLabel() {
      return this.cycleAdjustmentLabel(this.cycle)
    },

    cycleAdjustmentLabel(months) {
      const c = this.cyclePricing(months)
      if (!c) return null
      if (c.savings_percent > 0) return config.labels.savings.replace("%{percent}", this.formatPercent(c.savings_percent))
      return null
    },

    formatPercent(value) {
      return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(".", ",")
    },

    // Switching tier keeps the platform count when the new tier offers it,
    // otherwise drops to that tier's entry plan rather than showing a
    // selection the customer can't actually buy.
    selectTier(tier) {
      this.tier = tier
      const available = (this.catalogue[tier] || []).map((p) => p.platforms)
      if (!available.includes(this.platforms)) this.platforms = available[0]
    },

    toggle(section) {
      this.section = this.section === section ? null : section
    },

    // Jump straight to a sub-section from the summary's pencil affordances.
    edit(section, step) {
      this.section = section
      if (step) this.step = step
    },

    priceFor(plan, cycle) {
      const p = plan.cycles[String(cycle)]
      return p ? p.total : 0
    },

    // Pricing for the current plan on an arbitrary cycle — drives the billing
    // cycle rows, which each show their own total and saving.
    cyclePricing(months) {
      return (this.plan && this.plan.cycles[String(months)]) || { total: 0, effective_monthly: 0, adjustment_percent: 0, savings_percent: 0, currency: "EUR" }
    },

    platformLabel(count) {
      return count === 1 ? config.labels.platform : config.labels.platforms
    },

    // Currency-aware: EUR renders with its symbol, anything else spells out
    // the ISO code, since Intl only has symbols wired up for currencies it knows.
    money(value, currency) {
      const code = (currency || (this.plan && this.plan.currency) || "EUR").toUpperCase()
      const currencyDisplay = code === "EUR" ? "symbol" : "code"
      try {
        return new Intl.NumberFormat(config.locale || "de-DE", { style: "currency", currency: code, currencyDisplay }).format(value || 0)
      } catch (e) {
        return `${(value || 0).toFixed(2)} ${code}`
      }
    },

    cycleLabel(months) {
      return config.cycleLabels[String(months)] || `${months}`
    },
  }
}
